import cn from "classnames";
import { type FC } from "react";
import { isFunction } from "lodash";
import EllipsisPopupButton from "components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import { ArchiveLinkButton, EditLinkButton, RemoveLinkButton, RestoreLinkButton } from "components/buttons/linkButtons";
import { Tooltip } from "components/common/tooltip";
import { PricingTypes } from "enums/licensing/pricingTypes";

export interface Props {
  id: number;
  title: string;
  thumbnailImageUrl: string;
  isExpired: boolean;
  isTrial: boolean;
  packPricingTypeId?: number;
  isArchived: boolean;
  hasAccountManageRole: boolean;
  onEditLicensing?: (id: number, packPricingTypeId?: number) => void;
  onConvertTrialLicense?: (id: number, packPricingTypeId?: number) => void;
  onExtendLicenses?: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  onRemovePackFromAccount?: (id: number) => void;
  isAllowedToRemovePackFromAccount?: () => boolean;
  onArchivePack?: (id: number) => void;
  onRestorePack?: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  onTogglePaymentModal?: () => void;
  hasPurchasePower: boolean;
  isAccountHome: boolean;
  isOwnPack: boolean;
  hasSource: boolean;
  licenseCount: number;
  hasPurchasePackAddon: boolean;
  expirationDate?: Date;
  hasGlobalAccess: boolean;
  pricePerUserPerMonth?: number;
  billingTerm?: string;
  isPurchased?: boolean;
}

export const AccountPackMenu: FC<Props> = (props: Props) => {
  const {
    id,
    title,
    thumbnailImageUrl,
    isExpired,
    isArchived,
    onEditLicensing,
    onExtendLicenses,
    onRemovePackFromAccount,
    onConvertTrialLicense,
    isAllowedToRemovePackFromAccount,
    onArchivePack,
    hasAccountManageRole,
    onRestorePack,
    onTogglePaymentModal,
    hasPurchasePower,
    isAccountHome,
    isOwnPack,
    hasSource,
    packPricingTypeId,
    licenseCount,
    expirationDate,
    hasPurchasePackAddon,
    hasGlobalAccess,
    isTrial,
    isPurchased = true,
  } = props;

  const isFree = packPricingTypeId === PricingTypes.Free;
  const isPaid = packPricingTypeId === PricingTypes.Paid;
  const shouldShowPurchaseButton =
    isAccountHome &&
    isPaid &&
    hasPurchasePower &&
    hasPurchasePackAddon &&
    !isOwnPack &&
    (!isPurchased || isTrial) &&
    !isArchived;

  const convertTrialLicenseButton = !isExpired && isPaid && isTrial && !isOwnPack && hasGlobalAccess && !isAccountHome;

  const shouldShowExtendLicensingButton =
    isExpired && !isArchived && onExtendLicenses && !isOwnPack && hasGlobalAccess && !isAccountHome;

  const getEditLicensingButton = (isDisabled: boolean) => {
    if (isDisabled) {
      return (
        <Tooltip
          content={"You don't have permission to perform this action. Contact you administrator for more information."}
          target={
            <EditLinkButton
              key={cn("EditLicensingButton-", id)}
              onClick={() => {
                onEditLicensing && onEditLicensing(id, packPricingTypeId);
              }}
              text="Edit Licensing"
              isDisabled
            />
          }
          showAlways
        />
      );
    }

    return (
      <EditLinkButton
        key={cn("EditLicensingButton-", id)}
        onClick={() => {
          onEditLicensing && onEditLicensing(id, packPricingTypeId);
        }}
        text="Edit Licensing"
      />
    );
  };

  const extendLicensesButton = (
    <EditLinkButton
      key={cn("ExtendLicensesButton-", id)}
      onClick={() => {
        onExtendLicenses?.(id, title, thumbnailImageUrl, packPricingTypeId);
      }}
      text="Edit Licensing"
    />
  );

  const convertTrialLicensesButton = (
    <EditLinkButton
      key={cn("ConvertTrialLicensesButton-", id)}
      onClick={() => {
        onConvertTrialLicense?.(id, packPricingTypeId);
      }}
      text="Edit Licensing"
    />
  );

  const removeButton = (
    <RemoveLinkButton
      key={cn("RemovePackButton-", id)}
      onClick={() => {
        onRemovePackFromAccount?.(id);
      }}
    />
  );

  const archiveButton = (
    <ArchiveLinkButton
      key={cn("ArchivePackButton-", id)}
      onClick={() => {
        onArchivePack?.(id);
      }}
    />
  );

  const restoreButton = (
    <RestoreLinkButton
      key={cn("RestorePackButton-", id)}
      onClick={() => {
        onRestorePack?.(id, title, thumbnailImageUrl, packPricingTypeId);
      }}
    />
  );

  const purchaseNowButton = (
    <EditLinkButton
      isDisabled={isArchived}
      key={cn("PurchaseNowButton-", id)}
      onClick={onTogglePaymentModal}
      text="Purchase now"
    />
  );

  const getButtons = () => {
    const actionButtons = [];

    const shouldShowEditLicensingButton = () => {
      if (isTrial || isExpired || (!isFree && !hasGlobalAccess) || isOwnPack) return false;

      const hasLicenseLimits = licenseCount > 0;
      const hasExpirationDate = !!expirationDate;

      if (!hasLicenseLimits && !hasExpirationDate) return false;

      if (hasGlobalAccess && !hasPurchasePackAddon) {
        return hasLicenseLimits || hasExpirationDate;
      }

      return hasPurchasePackAddon && (hasLicenseLimits || hasExpirationDate) && !isOwnPack;
    };

    const shouldDisableEditLicensingButton =
      !hasGlobalAccess && hasPurchasePackAddon && isFree && (licenseCount > 0 || !!expirationDate) && !hasPurchasePower;

    if (shouldShowEditLicensingButton()) {
      actionButtons.push(getEditLicensingButton(shouldDisableEditLicensingButton));
    }

    if (shouldShowPurchaseButton) {
      actionButtons.push(purchaseNowButton);
    }

    if (convertTrialLicenseButton) {
      actionButtons.push(convertTrialLicensesButton);
    }

    if (shouldShowExtendLicensingButton) {
      actionButtons.push(extendLicensesButton);
    }

    if (
      isFunction(onRemovePackFromAccount) &&
      isFunction(isAllowedToRemovePackFromAccount) &&
      isAllowedToRemovePackFromAccount?.()
    ) {
      actionButtons.push(removeButton);
    }

    if (onArchivePack && !isOwnPack) {
      actionButtons.push(archiveButton);
    }

    const shouldShowRestoreButton = () => {
      if (!onRestorePack || isOwnPack) return false;

      if (hasGlobalAccess) return true;

      if (isAccountHome) return hasPurchasePower && isFree;

      return isFree;
    };

    if (shouldShowRestoreButton()) {
      actionButtons.push(restoreButton);
    }

    return actionButtons;
  };

  const buttons = getButtons();

  const renderMenuContent = () => {
    const hasButtons = buttons.length > 0;
    const shouldShowMenu = hasAccountManageRole && !hasSource;

    if (shouldShowMenu && hasButtons) {
      return <EllipsisPopupButton circle>{[buttons]}</EllipsisPopupButton>;
    }

    if (!shouldShowMenu) {
      return (
        <div data-testid="disabledCardEllipsis" onClick={(e) => e.stopPropagation()}>
          <EllipsisPopupButton disabled circle>
            {[]}
          </EllipsisPopupButton>
        </div>
      );
    }

    return null;
  };

  return renderMenuContent();
};
